import useToast from "@repo/core/components/core/Toast";
import { useDimensions } from "@repo/core/hooks/useDimensions";
import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useSearchParams } from "@repo/core/hooks/useSearchParams";
import { ROUTES } from "@repo/core/routes";
import { UnicoResource } from "@repo/core/services/resources/unico";
import { RequisitionsResource } from "@repo/core/services/resources/requisitions";
import {
  UnicoCreateProcessResponse,
  Flow
} from "@repo/core/services/entities/Unico";
import { newApInstance } from "@repo/core/services/instances/new-api";
import { wayupApiInstance } from "@repo/core/services/instances/wayup-api";
import { SessionExpiredFlowError } from "@repo/core/services/session/sessionEpoch";
import { persistPreProposalAlterarDataConferencePending } from "../../services/persistPreProposalAlterarPending.service";
import { useUploadProposalDocument } from "@repo/core/hooks/useUploadProposalDocument";
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import useAuthStore from "@repo/core/stores/auth";
import { normalizeUnicoCaptureMaxAttempts } from "@repo/core/utils/unicoCaptureAttempts";

import { RequiredDocumentTypeEnum } from "@repo/core/enums/proposals";
import { WEB_BIOMETRY_STORAGE } from "./utils";
import {
  getPendingManualDocuments,
  mergeUnicoUploadedIdentityTypes,
  pickUnicoIdentityTypesFromUploadedSet
} from "./utils";
import { STATUS_MESSAGES } from "./constants";
import {
  createMemoizedValidatedOcrDataLoader,
  mergeBiometryProposalAlterData
} from "./mappers";
import {
  PreProposalData,
  usePreProposalStore
} from "../../store/preProposalStore";
import { usePreProposal } from "../../hooks/usePreProposal";
import { useUnicoProcess } from "./hooks/useUnicoProcess";
import { useRequiredDocuments } from "./hooks/useRequiredDocuments";
import { useUnicoDocuments } from "./hooks/useUnicoDocuments";
import {
  navigateBasedOnDocuments,
  hasUnicoDocuments,
  navigateToReanalysisCreditFlowIfApplicable
} from "./services/navigation.service";
import { BIOMETRICS_FLOW } from "@repo/core/services/entities/Biometrics";
import { useCreditProposal } from "@repo/core/screens/ProposalEffectiveFlows/Credit/hooks/useCreditProposal";
import { enrichProcessDataWithSelfieWhenMissing } from "./helpers/enrichProcessDataWithSelfie.helper";
import { registerUnicoSelfieStepAfterDocumentUploads } from "./helpers/registerUnicoSelfieStep.helper";
import { shouldRetryUnicoCaptureForMissingArtifacts } from "./helpers/unicoCaptureRetry.helper";

const requisitionsResource = new RequisitionsResource(wayupApiInstance);

export const BiometryValidationController = () => {
  const { back, push, replace, resetTo } = useNavigation();
  const { clearCreditProposal } = useCreditProposal();
  const toast = useToast();
  const searchParams = useSearchParams();
  const { isTabletOrWeb } = useDimensions();

  const {
    proposalData,
    setProposalData,
    setRequiredDocuments,
    requiredDocuments,
    markDocumentAsUploaded,
    areAllDocumentsUploaded
  } = usePreProposal();

  const { uploadDocumentFromBase64 } = useUploadProposalDocument();

  // Initialize status message based on whether user is returning from Unico
  const hasVerificationId = !!searchParams?.verificationId;
  const [statusMessage, setStatusMessage] = useState(
    hasVerificationId
      ? STATUS_MESSAGES.VERIFYING_RESULT
      : STATUS_MESSAGES.INITIAL
  );
  const unicoCaptureMaxAttempts = useAuthStore((state) =>
    normalizeUnicoCaptureMaxAttempts(state.user?.unicoCaptureMaxAttempts)
  );
  const emptyCaptureAttemptCountRef = useRef(0);
  const emptyCaptureAttemptStorageKey = useMemo(() => {
    const proposalKey =
      proposalData?.lngProposta ?? proposalData?.strCliente ?? "current";
    return `unico-empty-capture-attempts:${proposalKey}`;
  }, [proposalData?.lngProposta, proposalData?.strCliente]);

  // Update status message when verificationId is detected
  useEffect(() => {
    if (hasVerificationId) {
      setStatusMessage(STATUS_MESSAGES.VERIFYING_RESULT);
    }
  }, [hasVerificationId]);

  // CRITICAL: When verificationId exists, DO NOT execute useRequiredDocuments query
  const {
    requiredDocuments: requiredDocumentsFromQuery,
    isRequiredDocumentsContextReady
  } = useRequiredDocuments({
    proposalData: proposalData || null,
    setRequiredDocuments,
    setProposalData,
    setStatusMessage: (message: string) => {
      if (!hasVerificationId) {
        setStatusMessage(message as STATUS_MESSAGES);
      }
    }
  });

  const { getDocumentsWithRetry, handleUploadDocuments } = useUnicoDocuments({
    proposalData: proposalData || null,
    requiredDocuments,
    markDocumentAsUploaded,
    uploadDocumentFromBase64
  });

  const navigateToManualCapture = useCallback(
    (options?: { forceManualCapture?: boolean; unavailable?: boolean }) => {
      const { forceManualCapture = false, unavailable = false } = options || {};

      setProposalData(
        {
          shouldForceManualCapture: forceManualCapture,
          restartRequired: false,
          unicoStatus: unavailable ? "unavailable" : "available"
        },
        { skipAlterarSync: true }
      );
      setStatusMessage(
        unavailable
          ? STATUS_MESSAGES.UNICO_UNAVAILABLE
          : STATUS_MESSAGES.MANUAL_PENDING
      );

      if (unavailable) {
        toast.error(
          "A plataforma de captura está indisponível no momento.",
          "Continue com o envio manual."
        );
      }

      replace(ROUTES.preProposalsSelectDocuments, {
        forceManualCapture,
        unicoUnavailable: unavailable
      });
    },
    [replace, setProposalData, toast]
  );

  const handleRestartRequired = useCallback(() => {
    setProposalData(
      {
        restartRequired: true,
        shouldForceManualCapture: false
      },
      { skipAlterarSync: true }
    );
    setStatusMessage(STATUS_MESSAGES.RESTART_REQUIRED);
    toast.error(
      "Não foi possível reaproveitar a captura anterior.",
      "Será necessário reiniciar a captura."
    );
    replace(ROUTES.preProposalsSelectDocuments, {
      restartRequired: true
    });
  }, [replace, setProposalData, toast]);

  // Create ref first
  const handleFinishValidationRef = useRef<
    ((processId?: string) => Promise<void>) | null
  >(null);

  // Store processId to be processed when handleOnFinishValidation is ready
  const pendingProcessIdRef = useRef<string | null>(null);

  // State to trigger useEffect when pending processId changes
  const [pendingProcessId, setPendingProcessId] = useState<string | null>(null);

  const {
    processId,
    token,
    validationURL,
    isFinishedValidation,
    setIsFinishedValidation,
    handleRetry,
    handleCreateProcess,
    resetProcessState,
    reuseCurrentProcess,
    registerBiometryStepLog
  } = useUnicoProcess({
    proposalData: proposalData || null,
    setStatusMessage: (message: string) => {
      setStatusMessage(message as STATUS_MESSAGES);
    },
    callbacks: {
      onSuccess: (processIdFromCallback: string) => {
        // Always store processId - useEffect will handle execution
        pendingProcessIdRef.current = processIdFromCallback;
        setPendingProcessId(processIdFromCallback); // Trigger useEffect

        // Try to call immediately if ref is ready
        if (handleFinishValidationRef.current) {
          const processIdToProcess = pendingProcessIdRef.current;
          pendingProcessIdRef.current = null;
          setPendingProcessId(null);
          handleFinishValidationRef.current(processIdToProcess);
        }
      },
      onError: (error) => {
        const message = error instanceof Error ? error.message : "";
        if (message.includes("Could not resolve bioTokenId")) {
          toast.warning(
            "Referência biométrica não encontrada.",
            "Não foi possível localizar o bioTokenId para o fluxo idtoken. Continue com a captura manual."
          );
          navigateToManualCapture({ forceManualCapture: true });
          return;
        }
        navigateToManualCapture({ forceManualCapture: true });
      },
      onUnavailable: () => {
        navigateToManualCapture({
          forceManualCapture: true,
          unavailable: true
        });
      }
    },
    requiredDocuments:
      requiredDocumentsFromQuery ?? requiredDocuments ?? undefined,
    isFetchedRequiredDocuments:
      hasVerificationId || isRequiredDocumentsContextReady
  });

  const getEmptyCaptureAttemptCount = useCallback(() => {
    return (
      WEB_BIOMETRY_STORAGE.getNumber(emptyCaptureAttemptStorageKey) ??
      emptyCaptureAttemptCountRef.current
    );
  }, [emptyCaptureAttemptStorageKey]);

  const setEmptyCaptureAttemptCount = useCallback(
    (attemptCount: number) => {
      emptyCaptureAttemptCountRef.current = attemptCount;
      WEB_BIOMETRY_STORAGE.setNumber(
        emptyCaptureAttemptStorageKey,
        attemptCount
      );
    },
    [emptyCaptureAttemptStorageKey]
  );

  const resetEmptyCaptureAttemptCount = useCallback(() => {
    emptyCaptureAttemptCountRef.current = 0;
    WEB_BIOMETRY_STORAGE.removeNumber(emptyCaptureAttemptStorageKey);
  }, [emptyCaptureAttemptStorageKey]);

  const retryUnicoCaptureAfterMissingArtifacts = useCallback(
    async (flow: Flow) => {
      const nextAttemptCount = getEmptyCaptureAttemptCount() + 1;

      if (nextAttemptCount >= unicoCaptureMaxAttempts) {
        resetEmptyCaptureAttemptCount();
        return false;
      }

      setEmptyCaptureAttemptCount(nextAttemptCount);
      resetProcessState();
      setIsFinishedValidation(false);
      setStatusMessage(STATUS_MESSAGES.STARTING_PROCESS);
      await handleCreateProcess(flow, { forceNewProcess: true });
      return true;
    },
    [
      getEmptyCaptureAttemptCount,
      handleCreateProcess,
      resetEmptyCaptureAttemptCount,
      resetProcessState,
      setEmptyCaptureAttemptCount,
      setIsFinishedValidation,
      unicoCaptureMaxAttempts
    ]
  );

  // Define handleOnFinishValidation after all hooks are called
  const handleOnFinishValidation = useCallback(
    async (_processId?: string) => {
      // If no processId provided, try to get from proposalData
      let currentProcessId = _processId ?? processId;
      if (!currentProcessId && proposalData?.strIdProcessoUnico) {
        currentProcessId = proposalData.strIdProcessoUnico;
      }

      if (!currentProcessId) {
        setStatusMessage(STATUS_MESSAGES.ERROR);
        toast.error(
          "Erro: ProcessId não encontrado. Por favor, tente novamente."
        );
        return;
      }

      try {
        const callProposalAlterAfterBiometryOcr = async (
          updatedProposalData: PreProposalData,
          ocrData: Partial<PreProposalData>
        ) => {
          const storeSnapshot =
            usePreProposalStore.getState().proposalData ?? {};
          const merged = mergeBiometryProposalAlterData({
            storeSnapshot,
            updatedProposalData,
            ocrData
          });
          if (!merged.lngProposta) {
            return;
          }
          setStatusMessage(STATUS_MESSAGES.UPDATING_PROPOSAL);
          await persistPreProposalAlterarDataConferencePending(merged);
          setProposalData(merged, { skipAlterarSync: true });
        };

        setIsFinishedValidation(true);
        setStatusMessage(STATUS_MESSAGES.VERIFYING_RESULT);

        const unicoResourceForRead = new UnicoResource(newApInstance);
        const docs = requiredDocuments || [];
        const requiresBiometry = docs.some(
          (document) =>
            String(document.strTipoDocumento).trim() ===
            RequiredDocumentTypeEnum.BIOMETRY
        );
        const requiresDocumentFront = docs.some(
          (document) =>
            String(document.strTipoDocumento).trim() ===
            RequiredDocumentTypeEnum.DOCUMENT_FRONT
        );
        const requiresDocumentBack = docs.some(
          (document) =>
            String(document.strTipoDocumento).trim() ===
            RequiredDocumentTypeEnum.DOCUMENT_BACK
        );

        const isOnlyBiometryRequired =
          requiresBiometry && !requiresDocumentFront && !requiresDocumentBack;
        const hasRequiredIdentityDocuments =
          requiresDocumentFront || requiresDocumentBack;

        const processDataResponse =
          await unicoResourceForRead.getProcess(currentProcessId);

        let processData = processDataResponse.data;

        const processFlow = processData.process?.flow;
        const isSelfieOnlyFlow =
          processFlow === Flow.idtoken || processFlow === Flow.idcheck;
        const requiresUnicoIdentityDocument =
          processFlow === Flow.iddocs && hasRequiredIdentityDocuments;
        const flowForRetry = (processFlow as Flow | undefined) ?? Flow.iddocs;

        const latestProposalBeforeBiometryLog =
          usePreProposalStore.getState().proposalData;
        if (latestProposalBeforeBiometryLog?.lngProposta) {
          await persistPreProposalAlterarDataConferencePending({
            ...latestProposalBeforeBiometryLog,
            strIdProcessoUnico:
              currentProcessId ||
              latestProposalBeforeBiometryLog.strIdProcessoUnico
          });
        }

        // RN012: Register consultation BEFORE recovering artifacts (selfie/documents)
        try {
          await registerBiometryStepLog({
            step: BIOMETRICS_FLOW.PROCESS_RESULT,
            lngProposta: proposalData?.lngProposta || 0,
            response: { process: processData.process } as object
          });
        } catch {
          // Do not block flow
        }

        processData = await enrichProcessDataWithSelfieWhenMissing(
          unicoResourceForRead,
          currentProcessId,
          processData,
          requiresBiometry
        );

        const getValidatedOcrData = createMemoizedValidatedOcrDataLoader({
          currentProposalData: proposalData,
          lookupNameGender: async (name) => {
            const { data } = await requisitionsResource.getNameGender(name);
            return data.genero;
          }
        });

        const uploadSelfieArtifactIfAvailable = async (
          biomProcessData: UnicoCreateProcessResponse
        ): Promise<void> => {
          if (!biomProcessData.selfie?.fileContents) {
            return;
          }
          try {
            const uploadResult = await handleUploadDocuments(biomProcessData, {
              documentFrontBase64: "",
              documentBackBase64: ""
            });
            setProposalData(
              {
                unicoUploadedIdentityDocumentTypes:
                  mergeUnicoUploadedIdentityTypes(
                    proposalData?.unicoUploadedIdentityDocumentTypes,
                    uploadResult.uploadedIdentityTypes
                  )
              },
              { skipAlterarSync: true }
            );
            await registerUnicoSelfieStepAfterDocumentUploads(
              registerBiometryStepLog,
              proposalData?.lngProposta,
              uploadResult.lngDocumentoForSelfieRegistration,
              biomProcessData.selfie
            );
          } catch {
            // Do not block flow
          }
        };

        const finalizeBiometryOnlyFlow = async (
          biometryProcessData: UnicoCreateProcessResponse,
          biometryProcessId: string
        ) => {
          resetEmptyCaptureAttemptCount();
          const ocrData = await getValidatedOcrData(biometryProcessData);
          const updatedProposalData = {
            ...proposalData,
            strIdProcessoUnico: biometryProcessId,
            unicoStatus: "available" as const,
            shouldForceManualCapture: false,
            shouldForceNewBiometry: false,
            biometryReuseProcessId: undefined,
            restartRequired: false,
            ...ocrData
          };
          if (updatedProposalData.lngProposta) {
            await callProposalAlterAfterBiometryOcr(
              updatedProposalData,
              ocrData
            );
          } else {
            setProposalData(updatedProposalData, { skipAlterarSync: true });
          }

          if (searchParams.verificationId) {
            WEB_BIOMETRY_STORAGE.remove(searchParams.verificationId ?? "");
          }

          await uploadSelfieArtifactIfAvailable(biometryProcessData);

          if (
            navigateToReanalysisCreditFlowIfApplicable(
              updatedProposalData,
              replace,
              clearCreditProposal
            )
          ) {
            return;
          }
          setStatusMessage(STATUS_MESSAGES.SUCCESS);
          push(ROUTES.preProposalsVerifyInformations);
        };

        if (
          !requiresUnicoIdentityDocument &&
          shouldRetryUnicoCaptureForMissingArtifacts({
            processData,
            documentData: null,
            requiresBiometry,
            requiresIdentityDocument: false
          })
        ) {
          const retried =
            await retryUnicoCaptureAfterMissingArtifacts(flowForRetry);

          if (retried) {
            return;
          }

          navigateToManualCapture({ forceManualCapture: true });
          return;
        }

        if (
          (isOnlyBiometryRequired ||
            (isSelfieOnlyFlow && !hasRequiredIdentityDocuments)) &&
          (processData.process || processData.selfie?.fileContents)
        ) {
          await finalizeBiometryOnlyFlow(processData, currentProcessId);
          return;
        }

        let documentDataResponse;
        try {
          documentDataResponse = await getDocumentsWithRetry(currentProcessId);
        } catch {
          if (
            shouldRetryUnicoCaptureForMissingArtifacts({
              processData,
              documentData: null,
              requiresBiometry,
              requiresIdentityDocument: requiresUnicoIdentityDocument
            })
          ) {
            const retried =
              await retryUnicoCaptureAfterMissingArtifacts(flowForRetry);

            if (retried) {
              return;
            }

            navigateToManualCapture({ forceManualCapture: true });
            return;
          }

          resetEmptyCaptureAttemptCount();

          const ocrData = await getValidatedOcrData(processData);
          const updatedProposalData = {
            ...proposalData,
            strIdProcessoUnico: currentProcessId,
            unicoStatus: "available" as const,
            shouldForceManualCapture: false,
            restartRequired: false,
            ...ocrData
          };

          if (
            (isOnlyBiometryRequired ||
              (isSelfieOnlyFlow && !hasRequiredIdentityDocuments)) &&
            processData.selfie?.fileContents
          ) {
            await finalizeBiometryOnlyFlow(processData, currentProcessId);
            return;
          }

          if (updatedProposalData.lngProposta) {
            await callProposalAlterAfterBiometryOcr(
              updatedProposalData,
              ocrData
            );
          } else {
            setProposalData(updatedProposalData, { skipAlterarSync: true });
          }

          if (isSelfieOnlyFlow && hasRequiredIdentityDocuments) {
            await uploadSelfieArtifactIfAvailable(processData);
            navigateToManualCapture({
              forceManualCapture: true
            });
            return;
          }

          if (proposalData?.blnExisteRegistroUnico) {
            handleRestartRequired();
          } else {
            navigateToManualCapture({
              forceManualCapture: true
            });
          }
          return;
        }
        const documentData = documentDataResponse.data;
        const isReuseFullDocumentPath = Boolean(
          documentData.fullDocumentBase64?.trim()
        );

        if (processData.process) {
          if (
            shouldRetryUnicoCaptureForMissingArtifacts({
              processData,
              documentData,
              requiresBiometry,
              requiresIdentityDocument: requiresUnicoIdentityDocument
            })
          ) {
            const retried =
              await retryUnicoCaptureAfterMissingArtifacts(flowForRetry);

            if (retried) {
              return;
            }

            navigateToManualCapture({ forceManualCapture: true });
            return;
          }

          resetEmptyCaptureAttemptCount();

          let uploadIdentityFromSdk: string[] = [];
          let uploadResult: {
            uploadedSelfie: boolean;
            uploadedIdentityTypes: string[];
            reusedIdentityUploadFailed: boolean;
            lngDocumentoForSelfieRegistration?: string;
          } | null = null;

          if (
            documentData.documentFrontBase64 ||
            documentData.documentBackBase64 ||
            documentData.fullDocumentBase64?.trim() ||
            processData.selfie?.fileContents
          ) {
            setStatusMessage(STATUS_MESSAGES.SAVE_DOCUMENT_DATA);

            try {
              uploadResult = await handleUploadDocuments(
                processData,
                documentData
              );
              uploadIdentityFromSdk = uploadResult.uploadedIdentityTypes;
              await registerUnicoSelfieStepAfterDocumentUploads(
                registerBiometryStepLog,
                proposalData?.lngProposta,
                uploadResult.lngDocumentoForSelfieRegistration,
                processData.selfie
              );
            } catch (uploadError) {
              toast.error("Erro ao salvar documentos. Continuando o fluxo...");
            }
          }

          if (uploadResult?.reusedIdentityUploadFailed) {
            toast.error(
              "Não foi possível enviar o documento de identidade reaproveitado.",
              "Continuando com as etapas restantes."
            );
          }

          const ocrData = await getValidatedOcrData(processData);
          const updatedProposalData = {
            ...proposalData,
            strIdProcessoUnico: currentProcessId,
            unicoStatus: "available" as const,
            shouldForceManualCapture: false,
            shouldForceNewBiometry: false,
            biometryReuseProcessId: undefined,
            restartRequired: false,
            ...ocrData
          };

          if (searchParams.verificationId) {
            WEB_BIOMETRY_STORAGE.remove(searchParams.verificationId ?? "");
          }

          const hasMissingDocumentFront = isReuseFullDocumentPath
            ? true
            : !documentData.documentFrontBase64;
          const hasMissingDocumentBack = isReuseFullDocumentPath
            ? true
            : !documentData.documentBackBase64;

          const noRecoveredArtifacts =
            !processData.selfie?.fileContents &&
            !documentData.documentFrontBase64 &&
            !documentData.documentBackBase64 &&
            !documentData.fullDocumentBase64?.trim();

          if (
            noRecoveredArtifacts &&
            proposalData?.blnExisteRegistroUnico &&
            (requiresBiometry || requiresDocumentFront || requiresDocumentBack)
          ) {
            if (updatedProposalData.lngProposta) {
              await callProposalAlterAfterBiometryOcr(
                updatedProposalData,
                ocrData
              );
            } else {
              setProposalData(updatedProposalData, { skipAlterarSync: true });
            }

            handleRestartRequired();
            return;
          }

          const selfieUploadedToProposal =
            uploadResult?.uploadedSelfie === true;

          const needsManualUnicoCapture =
            (requiresBiometry && !selfieUploadedToProposal) ||
            (hasMissingDocumentFront && requiresDocumentFront) ||
            (hasMissingDocumentBack && requiresDocumentBack);

          const uploadedDocumentTypes = new Set(
            docs
              .filter((document) => document.isUploaded === true)
              .map((document) => String(document.strTipoDocumento).trim())
          );

          if (selfieUploadedToProposal) {
            uploadedDocumentTypes.add(RequiredDocumentTypeEnum.BIOMETRY);
          }

          if (!hasMissingDocumentFront) {
            uploadedDocumentTypes.add(RequiredDocumentTypeEnum.DOCUMENT_FRONT);
          }

          if (!hasMissingDocumentBack) {
            uploadedDocumentTypes.add(RequiredDocumentTypeEnum.DOCUMENT_BACK);
          }

          for (const identityType of uploadIdentityFromSdk) {
            uploadedDocumentTypes.add(String(identityType).trim());
          }

          let manualCaptureExcludedDocumentTypes = [
            ...(proposalData?.manualCaptureExcludedDocumentTypes ?? [])
          ];
          if (uploadResult?.reusedIdentityUploadFailed) {
            manualCaptureExcludedDocumentTypes = [
              ...new Set([
                ...manualCaptureExcludedDocumentTypes,
                RequiredDocumentTypeEnum.REUSED_IDENTITY_DOCUMENT
              ])
            ];
          } else if (
            isReuseFullDocumentPath &&
            uploadIdentityFromSdk.includes(
              RequiredDocumentTypeEnum.REUSED_IDENTITY_DOCUMENT
            )
          ) {
            manualCaptureExcludedDocumentTypes =
              manualCaptureExcludedDocumentTypes.filter(
                (t) =>
                  String(t).trim() !==
                  RequiredDocumentTypeEnum.REUSED_IDENTITY_DOCUMENT
              );
          }

          const manualExcludedTypeSet = new Set(
            manualCaptureExcludedDocumentTypes.map((t) => String(t).trim())
          );

          const mergedUnicoIdentity = mergeUnicoUploadedIdentityTypes(
            mergeUnicoUploadedIdentityTypes(
              proposalData?.unicoUploadedIdentityDocumentTypes,
              uploadIdentityFromSdk
            ),
            pickUnicoIdentityTypesFromUploadedSet(uploadedDocumentTypes)
          );

          const resolvedDocuments = docs.map((document) => ({
            ...document,
            isUploaded: uploadedDocumentTypes.has(
              String(document.strTipoDocumento).trim()
            )
          }));

          const areAllResolvedDocumentsUploaded =
            resolvedDocuments.length > 0 &&
            resolvedDocuments.every((document) => {
              const docType = String(document.strTipoDocumento).trim();
              if (manualExcludedTypeSet.has(docType)) {
                return true;
              }
              return document.isUploaded === true;
            });

          const pendingManualDocuments = getPendingManualDocuments(
            resolvedDocuments,
            {
              includePendingUnicoDocuments: needsManualUnicoCapture,
              unicoUploadedIdentityDocumentTypes: mergedUnicoIdentity,
              excludeDocumentTypesFromManual: manualCaptureExcludedDocumentTypes
            }
          );

          const finalProposalData = {
            ...updatedProposalData,
            shouldForceManualCapture: needsManualUnicoCapture,
            restartRequired: false,
            unicoUploadedIdentityDocumentTypes: mergedUnicoIdentity,
            manualCaptureExcludedDocumentTypes
          };

          if (finalProposalData.lngProposta) {
            await callProposalAlterAfterBiometryOcr(finalProposalData, ocrData);
          } else {
            setProposalData(finalProposalData, { skipAlterarSync: true });
          }

          if (pendingManualDocuments.length > 0) {
            setStatusMessage(STATUS_MESSAGES.MANUAL_PENDING);
            replace(ROUTES.preProposalsSelectDocuments, {
              forceManualCapture: needsManualUnicoCapture
            });
          } else if (
            areAllResolvedDocumentsUploaded ||
            areAllDocumentsUploaded()
          ) {
            if (
              navigateToReanalysisCreditFlowIfApplicable(
                finalProposalData,
                resetTo,
                clearCreditProposal
              )
            ) {
              return;
            }

            setStatusMessage(STATUS_MESSAGES.SUCCESS);
            replace(ROUTES.preProposalsVerifyInformations);
          } else {
            toast.error(
              "Ainda existem documentos pendentes. Por favor, tente novamente."
            );
          }
        }
      } catch (error) {
        if (error instanceof SessionExpiredFlowError) {
          return;
        }

        setStatusMessage(STATUS_MESSAGES.ERROR);

        const retried = await handleRetry();
        if (!retried) {
          navigateBasedOnDocuments(
            requiredDocuments,
            push,
            hasUnicoDocuments(requiredDocuments),
            proposalData,
            replace,
            clearCreditProposal,
            resetTo
          );
        }
      }
    },
    [
      setIsFinishedValidation,
      setStatusMessage,
      processId,
      proposalData,
      getDocumentsWithRetry,
      handleUploadDocuments,
      setProposalData,
      searchParams,
      requiredDocuments,
      push,
      replace,
      resetTo,
      clearCreditProposal,
      areAllDocumentsUploaded,
      registerBiometryStepLog,
      toast,
      handleRetry,
      retryUnicoCaptureAfterMissingArtifacts,
      resetEmptyCaptureAttemptCount,
      handleRestartRequired,
      navigateToManualCapture
    ]
  );

  // Set ref after handleOnFinishValidation is defined
  useEffect(() => {
    handleFinishValidationRef.current = handleOnFinishValidation;
  }, [handleOnFinishValidation]);

  // Execute pending callback when both ref and processId are ready
  useEffect(() => {
    if (pendingProcessId && handleFinishValidationRef.current) {
      const processIdToProcess = pendingProcessId;
      pendingProcessIdRef.current = null;
      setPendingProcessId(null);
      handleFinishValidationRef.current(processIdToProcess);
    }
  }, [pendingProcessId, handleOnFinishValidation]);

  /**
   * Handles WebView error - reuses the current process instead of creating a new one
   */
  const handleWebViewError = useCallback(() => {
    toast.error("Ocorreu um erro na validação. Tentando novamente...");
    reuseCurrentProcess();
  }, [reuseCurrentProcess, toast]);

  /**
   * Handles WebView close without completing - reuses the current process
   */
  const handleWebViewClose = useCallback(() => {
    reuseCurrentProcess();
  }, [reuseCurrentProcess]);

  return {
    back,
    isTabletOrWeb,
    handleOnFinishValidation,
    handleWebViewError,
    handleWebViewClose,
    statusMessage,
    validationURL,
    token,
    processId,
    isFinishedValidation
  };
};
